import { createTheme, ThemeOptions } from '@mui/material/styles';

// PolarRide Brand Colors - Refined
const colors = {
  // Gold - used sparingly as accent
  gold: {
    main: '#FFD700',
    light: '#FFED4E',
    dark: '#C5A600',
  },
  // Primary blacks and greys
  black: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    950: '#000000',
  },
  // Status colors - muted for professional look
  status: {
    success: '#10B981', // Emerald
    warning: '#F59E0B', // Amber
    error: '#EF4444', // Red
    info: '#3B82F6', // Blue
  }
};

// Create refined theme
const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: colors.black[900],
      light: colors.black[700],
      dark: colors.black[950],
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: colors.gold.main,
      light: colors.gold.light,
      dark: colors.gold.dark,
      contrastText: colors.black[900],
    },
    success: {
      main: colors.status.success,
      contrastText: '#FFFFFF',
    },
    warning: {
      main: colors.status.warning,
      contrastText: '#FFFFFF',
    },
    error: {
      main: colors.status.error,
      contrastText: '#FFFFFF',
    },
    info: {
      main: colors.status.info,
      contrastText: '#FFFFFF',
    },
    grey: colors.black,
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: colors.black[900],
      secondary: colors.black[600],
      disabled: colors.black[400],
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
        },
        contained: {
          backgroundColor: colors.black[900],
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: colors.black[800],
          },
        },
        containedSecondary: {
          backgroundColor: colors.gold.main,
          color: colors.black[900],
          '&:hover': {
            backgroundColor: colors.gold.dark,
          },
        },
        outlined: {
          borderColor: colors.black[300],
          color: colors.black[900],
          '&:hover': {
            borderColor: colors.black[400],
            backgroundColor: colors.black[50],
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
          border: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          border: 'none',
        },
        filled: {
          backgroundColor: colors.black[100],
          color: colors.black[700],
          '&.MuiChip-colorSecondary': {
            backgroundColor: colors.gold.light + '20',
            color: colors.gold.dark,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: colors.black[100],
          height: 6,
        },
        bar: {
          borderRadius: 4,
        },
        barColorPrimary: {
          backgroundColor: colors.black[900],
        },
        barColorSecondary: {
          backgroundColor: colors.gold.main,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.black[100],
          color: colors.black[700],
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 600,
        },
        colorSecondary: {
          backgroundColor: colors.gold.main,
          color: colors.black[900],
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.black[50],
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.black[100]}`,
        },
        head: {
          backgroundColor: colors.black[50],
          fontWeight: 600,
          color: colors.black[700],
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    // Gold accent only for special elements
    MuiRating: {
      styleOverrides: {
        iconFilled: {
          color: colors.gold.main,
        },
      },
    },
  },
};

export const refinedTheme = createTheme(themeOptions);

// Helper function for status colors
export const getStatusColor = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    active: colors.status.success,
    inactive: colors.black[500],
    pending: colors.status.warning,
    suspended: colors.status.error,
    rented: colors.gold.main,
    available: colors.status.success,
    verified: colors.status.success,
  };
  return statusMap[status.toLowerCase()] || colors.black[500];
};